/**
 * Derive the location line (building + floor) shown in the meeting-room picker
 * from a provider's generated room name, by stripping the trailing capacity and
 * the room name — both already shown separately in the picker. Shared by the
 * new ScheduleInterview wizard and the legacy KInterviewScheduler picker.
 *
 *  Google : "Chennai-2nd Floor-McLaren (10)"  -> "Chennai-2nd Floor"
 *           "Blk 67-02-24/25/26-02-Mercedes (5)" -> "Blk 67-02-24/25/26-02"
 *  Outlook: "Kula room 1" (== room_name)      -> "" (nothing extra to show)
 *
 * BE reference (kula/core): Google's `room_generated_name` comes from
 * `resource.generated_resource_name` and can be `nil` when a resource has no
 * building/floor metadata; Outlook sets it to the display name (== room_name).
 * Both are handled here — a missing/plain name yields "".
 */
export const getRoomLocation = (
  generatedName?: string | null,
  roomName?: string | null
): string => {
  if (!generatedName || typeof generatedName !== 'string') return '';

  // Drop the trailing capacity suffix, e.g. " (10)".
  let location = generatedName.replace(/\s*\(\d+\)\s*$/, '').trim();

  // Drop the trailing room name — Google appends it as the last segment
  // ("Building-Floor-Room"); Outlook returns just the room name, so this
  // collapses the value to "".
  const trimmedName = typeof roomName === 'string' ? roomName.trim() : '';
  if (trimmedName && location.endsWith(trimmedName)) {
    location = location
      .slice(0, location.length - trimmedName.length)
      .replace(/[\s\-–]+$/, '')
      .trim();
  }

  return location;
};
